/**
 * Returns true if the `block` passed in is a non-deleted direct child of the
 * block matching the `parent_id`.
 *
 * @param {Object} block - object containing data for the current block
 * @param {String} parent_id - the parent's block_id
 */

export function isDirectChild(block, parent_id) {
  return block.parent_id === parent_id && !block._destroy
}

/**
 * Returns the ordered, non-deleted blocks belonging to the block with a
 * `block_id` of `parent_id`.
 *
 * @param {Array} blocks - an array of blocks
 * @param {String} parent_id - `block_id` of the parent element
 */

export function getChildBlocks(blocks, parent_id) {
  return blocks
    .filter(block => isDirectChild(block, parent_id))
    .sort((a, b) => a.order - b.order)
}

/**
 * Looks up a block by its `block_id`. Returns the entire block object.
 *
 * @param {String} block_id
 * @param {Array} blocks
 */

export function getBlockById(block_id, blocks) {
  if (!blocks) return

  return blocks.find(block => block.block_id === block_id)
}

/**
 * Returns an array containing the ordered, non-deleted siblings of a block as
 * well as the block itself.
 *
 * @param {String} block_id
 * @param {Array} blocks
 */

export function getSiblingsForBlock(block_id, blocks) {
  const { parent_id } = getBlockById(block_id, blocks)
  return getChildBlocks(blocks, parent_id)
}

/**
 * Returns an array containing the blocks matching type `type`.
 *
 * @param {String} type
 * @param {Array} blocks
 */

export function getBlocksOfType(type, blocks) {
  return blocks.filter(block => block.type === type)
}

/**
 * Returns an array containing the ancestors of the given block ordered from
 * parent to child. The given block is included in the output.
 *
 * @param {String} block_id
 * @param {Array} blocks
 * @returns {Array}
 */

export function getAncestorTree(block_id, blocks) {
  // If there aren't any blocks, there aren't any ancestors.
  if (!blocks?.length) return []

  const nonDeletedBlocks = blocks.filter(b => !b._destroy)
  const targetBlock = nonDeletedBlocks.find(b => b.block_id === block_id)

  // If there's no target block, there aren't any ancestors.
  if (!targetBlock) return []

  return _getParents(targetBlock, nonDeletedBlocks, [])
}

function _getParents(block, blocks, newBlocks) {
  const parentBlock = blocks.find(b => b.block_id === block.parent_id)
  const result = [block, ...newBlocks]
  if (!parentBlock) return result
  return _getParents(parentBlock, blocks, result).filter(Boolean)
}
